/**
 * MFN comparison engine — rules mode.
 *
 * Build doc §4.2 Flow B and §10.4. The engine answers: "does LP A's MFN clause
 * entitle A to the term LP B received?" It is deliberately conservative in the
 * direction the build doc demands — false positives are recoverable (the CCO
 * dismisses them), false negatives create legal liability. When a candidate
 * provision is in-scope and the holder has no equivalent (or a worse) term,
 * the engine fires a trigger with an explanatory rationale.
 *
 * The Claude Opus path (§10.4) layers on top: it re-judges each rules-mode
 * trigger and can add nuance the rules miss; it never suppresses a trigger
 * silently (it can only lower confidence and enrich the rationale).
 */

import { ECONOMIC_CATEGORIES, type ProvisionCategory } from "./taxonomy";
import type { CandidateProvision, MfnClauseInput, MfnTriggerResult } from "./types";

export const MFN_ENGINE_VERSION = "mfn-rules-v1.0";

/** Categories an MFN never reaches: another LP's MFN is not an electable benefit. */
const NEVER_COMPARABLE: readonly ProvisionCategory[] = [
  "mfn",
  "most_favored_nation_express",
  "other",
];

export interface MfnEvaluationInput {
  clauses: MfnClauseInput[];
  /** Active, non-rejected provisions across all LPs in the fund. */
  provisions: CandidateProvision[];
  /** lpId → total commitment USD in this fund. */
  commitments: Record<string, number>;
}

function fmtUsd(n: number): string {
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1).replace(/\.0$/, "")}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  return `$${n.toLocaleString("en-US")}`;
}

/** Numeric fields `numericallyBetter` can compare, per category, in the order
 * they should be checked for the trigger rationale text. `higherIsBetter`
 * flips the min/max and comparison direction — true only for `hurdle_pct`
 * (a higher preferred return is better for the LP; every other field here is
 * "lower is better": a lower fee, lower carry, or lower expense cap). */
type RationaleField = "fee_pct" | "carry_pct" | "hurdle_pct" | "cap_usd" | "cap_pct";

const RATIONALE_FIELDS: Partial<
  Record<ProvisionCategory, readonly { field: RationaleField; higherIsBetter: boolean }[]>
> = {
  mgmt_fee_discount: [{ field: "fee_pct", higherIsBetter: false }],
  carry_modification: [
    { field: "carry_pct", higherIsBetter: false },
    { field: "hurdle_pct", higherIsBetter: true },
  ],
  expense_cap: [
    { field: "cap_usd", higherIsBetter: false },
    { field: "cap_pct", higherIsBetter: false },
  ],
  org_expense_cap: [
    { field: "cap_usd", higherIsBetter: false },
    { field: "cap_pct", higherIsBetter: false },
  ],
};

function fmtRationaleValue(field: RationaleField, n: number): string {
  return field === "cap_usd" ? fmtUsd(n) : `${n}%`;
}

/**
 * Builds the human-readable "why" for a fired trigger. Walks the category's
 * comparable fields in order and describes the first one where the candidate
 * actually beats a holder equivalent numerically; falls back to describing
 * the raw value the beneficiary received when no holder equivalent shares a
 * comparable field (existence-style firing, or a cross-representation case
 * `numericallyBetter` couldn't disprove — see its expense_cap/carry_modification
 * branches). 2026-07-03 audit fix: the previous version only ever read
 * fee_pct/carry_pct/cap_usd, so a %-only expense cap or a hurdle-only carry
 * change rendered "undefined%" in the rationale even when the trigger fired
 * correctly.
 */
function describeBetterTerm(
  category: ProvisionCategory,
  candidate: CandidateProvision,
  holderEquivalents: CandidateProvision[],
): string {
  const descriptors = RATIONALE_FIELDS[category] ?? [];
  for (const { field, higherIsBetter } of descriptors) {
    const candVal = candidate.structuredValue?.[field];
    if (candVal === undefined) continue;
    const holderVals = holderEquivalents
      .map((p) => p.structuredValue?.[field])
      .filter((v): v is number => v !== undefined);
    if (holderVals.length === 0) continue;
    const holderBest = higherIsBetter ? Math.max(...holderVals) : Math.min(...holderVals);
    const isBetter = higherIsBetter ? candVal > holderBest : candVal < holderBest;
    if (isBetter) {
      return `Beneficiary's term (${fmtRationaleValue(field, candVal)}) is more favorable than the holder's equivalent (${fmtRationaleValue(field, holderBest)}).`;
    }
  }
  const firstCandidateField = descriptors.find(
    (d) => candidate.structuredValue?.[d.field] !== undefined,
  );
  if (firstCandidateField) {
    const candVal = candidate.structuredValue?.[firstCandidateField.field];
    if (candVal !== undefined) {
      return `Beneficiary received ${fmtRationaleValue(firstCandidateField.field, candVal)} and the holder has no equivalent concession.`;
    }
  }
  return `Beneficiary holds a ${category.replaceAll("_", " ")} provision more favorable than the holder's on-file equivalent.`;
}

/**
 * Numeric "is the candidate better than the holder's best equivalent?" checks
 * for categories where structured values are comparable. Returns:
 *   true  — candidate is strictly better (trigger, high confidence)
 *   false — candidate is equal-or-worse (no trigger)
 *   null  — not numerically comparable (fall through to existence logic)
 */
function numericallyBetter(
  category: ProvisionCategory,
  candidate: CandidateProvision,
  holderEquivalents: CandidateProvision[],
): boolean | null {
  if (category === "mgmt_fee_discount") {
    const candFee = candidate.structuredValue?.fee_pct;
    if (candFee === undefined) return null;
    const holderFees = holderEquivalents
      .map((p) => p.structuredValue?.fee_pct)
      .filter((v): v is number => v !== undefined);
    if (holderFees.length === 0) return true; // holder pays headline rate — any discount is better
    return candFee < Math.min(...holderFees);
  }
  if (category === "carry_modification") {
    // 2026-07-03 audit fix (S1-3): `hurdle_pct` (the preferred return the GP
    // must clear before earning carry) is captured by extract.ts but used to
    // never be read here — only `carry_pct` was. A hurdle-only concession
    // (no `carry_pct` change) produced `candCarry === undefined` and fell
    // through to the existence-only branch below, which suppresses the
    // trigger the moment the holder has ANY carry_modification provision on
    // file, even one about an unrelated term. Direction: a HIGHER hurdle is
    // better for the LP (the GP must return more capital/profit before
    // earning carry) — the opposite direction from carry_pct, fee_pct, and
    // cap_usd/cap_pct, where LOWER is better.
    const candCarry = candidate.structuredValue?.carry_pct;
    const candHurdle = candidate.structuredValue?.hurdle_pct;
    if (candCarry === undefined && candHurdle === undefined) return null;

    const holderCarries = holderEquivalents
      .map((p) => p.structuredValue?.carry_pct)
      .filter((v): v is number => v !== undefined);
    const holderHurdles = holderEquivalents
      .map((p) => p.structuredValue?.hurdle_pct)
      .filter((v): v is number => v !== undefined);
    if (holderCarries.length === 0 && holderHurdles.length === 0) return true;

    let better = false;
    let comparable = false;
    if (candCarry !== undefined && holderCarries.length > 0) {
      comparable = true;
      if (candCarry < Math.min(...holderCarries)) better = true;
    }
    if (candHurdle !== undefined && holderHurdles.length > 0) {
      comparable = true;
      if (candHurdle > Math.max(...holderHurdles)) better = true;
    }
    if (better) return true;
    if (comparable) return false; // numerically compared on at least one axis, not better on either
    // Candidate has a carry/hurdle value but the holder's on-file
    // equivalent(s) don't share a comparable dimension (e.g. candidate is
    // hurdle-only, holder's provision is carry-only) — we cannot disprove a
    // real concession. Per the module's stated bias (false positives are
    // recoverable, false negatives are not), fire for review rather than
    // falling through to the existence check, which would silently suppress
    // it because the holder already has *a* carry_modification on file.
    return true;
  }
  if (category === "expense_cap" || category === "org_expense_cap") {
    // 2026-07-03 audit fix (S1-2): an expense cap expressed as a percentage
    // of commitment (`cap_pct`) used to be written into `fee_pct` by
    // extract.ts and never read here at all (only `cap_usd` was), so a
    // %-based cap fell through to the existence-only branch and was
    // suppressed by any unrelated $-based holder cap. `cap_pct` is now its
    // own field (types.ts) and is compared like-for-like against other
    // percentage caps, and against dollar caps when nothing else applies.
    const candCapUsd = candidate.structuredValue?.cap_usd;
    const candCapPct = candidate.structuredValue?.cap_pct;
    if (candCapUsd === undefined && candCapPct === undefined) return null;

    const holderCapsUsd = holderEquivalents
      .map((p) => p.structuredValue?.cap_usd)
      .filter((v): v is number => v !== undefined);
    const holderCapsPct = holderEquivalents
      .map((p) => p.structuredValue?.cap_pct)
      .filter((v): v is number => v !== undefined);
    if (holderCapsUsd.length === 0 && holderCapsPct.length === 0) return true;

    let better = false;
    let comparable = false;
    if (candCapUsd !== undefined && holderCapsUsd.length > 0) {
      comparable = true;
      if (candCapUsd < Math.min(...holderCapsUsd)) better = true; // lower $ cap = better protection
    }
    if (candCapPct !== undefined && holderCapsPct.length > 0) {
      comparable = true;
      if (candCapPct < Math.min(...holderCapsPct)) better = true; // lower % cap = better protection
    }
    if (better) return true;
    if (comparable) return false;
    // Genuinely mismatched representations (candidate's cap is a % of
    // commitment, the holder's on-file cap(s) are absolute dollars, or vice
    // versa) with nothing in the same unit to compare against directly. The
    // engine has no per-LP commitment context in this function to normalize
    // % → $ safely here, so — matching the carry/hurdle case above and the
    // module's stated false-positive bias — flag it for human review instead
    // of silently returning "no trigger" (the exact S1-2 failure mode).
    return true;
  }
  return null;
}

export function evaluateMfn(input: MfnEvaluationInput): MfnTriggerResult[] {
  const triggers: MfnTriggerResult[] = [];

  for (const clause of input.clauses) {
    const holderCommitment = input.commitments[clause.holderLpId] ?? null;

    // Gate: clause only operates if the holder committed at/above its threshold.
    if (
      clause.thresholdCommitmentUsd !== null &&
      holderCommitment !== null &&
      holderCommitment < clause.thresholdCommitmentUsd
    ) {
      continue;
    }

    const holderProvisionsByCategory = new Map<ProvisionCategory, CandidateProvision[]>();
    for (const p of input.provisions) {
      if (p.lpId !== clause.holderLpId) continue;
      const list = holderProvisionsByCategory.get(p.category) ?? [];
      list.push(p);
      holderProvisionsByCategory.set(p.category, list);
    }

    for (const candidate of input.provisions) {
      if (candidate.lpId === clause.holderLpId) continue;
      if (NEVER_COMPARABLE.includes(candidate.category)) continue;
      if (clause.excludedCategories.includes(candidate.category)) continue;
      if (clause.scope === "economic_only" && !ECONOMIC_CATEGORIES.includes(candidate.category)) {
        continue;
      }

      // Size-based MFN: only terms granted to equal-or-smaller LPs are electable.
      const beneficiaryCommitment = input.commitments[candidate.lpId] ?? null;
      if (
        clause.sizeBased &&
        holderCommitment !== null &&
        beneficiaryCommitment !== null &&
        beneficiaryCommitment > holderCommitment
      ) {
        continue;
      }

      const holderEquivalents = holderProvisionsByCategory.get(candidate.category) ?? [];
      const better = numericallyBetter(candidate.category, candidate, holderEquivalents);

      let fires: boolean;
      let confidence: number;
      let why: string;

      if (better === true) {
        fires = true;
        confidence = 0.9;
        why = describeBetterTerm(candidate.category, candidate, holderEquivalents);
      } else if (better === false) {
        continue; // numerically equal-or-worse — clean no-trigger
      } else {
        // Existence-based, conservative: in-scope benefit the holder lacks.
        if (holderEquivalents.length > 0) continue; // holder already has an equivalent-category term
        fires = true;
        confidence = 0.65;
        why = `Beneficiary holds a ${candidate.category.replaceAll("_", " ")} provision and the MFN holder has no equivalent. Flagged conservatively for review (engine cannot compare these terms numerically).`;
      }

      if (fires) {
        const scopeNote =
          clause.scope === "economic_only" ? " Clause scope: economic terms only." : "";
        const sizeNote =
          clause.sizeBased && beneficiaryCommitment !== null && holderCommitment !== null
            ? ` Size test passed (beneficiary ${fmtUsd(beneficiaryCommitment)} ≤ holder ${fmtUsd(holderCommitment)}).`
            : "";
        triggers.push({
          mfnClauseId: clause.id,
          triggeringProvisionId: candidate.id,
          holderLpId: clause.holderLpId,
          beneficiaryLpId: candidate.lpId,
          category: candidate.category,
          rationale: `${why}${scopeNote}${sizeNote}`,
          confidence,
        });
      }
    }
  }

  return triggers;
}
